import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tag, Mail, Phone, Calendar, ArrowLeft, User } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Comments from "@/components/Comments";
import useSeo from "@/hooks/useSeo";

const formatDateTime = (iso) => {
    try { return new Date(iso).toLocaleString("pl-PL", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

export default function OgloszenieDetailPage() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [labels, setLabels] = useState({});
    const [notFound, setNotFound] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        api.get(`/listings/${id}`)
            .then((r) => setItem(r.data))
            .catch(() => setNotFound(true));
        api.get("/listings/categories").then((r) => setLabels(r.data?.labels || {})).catch(() => {});
        if (typeof window !== "undefined" && window.localStorage.getItem("admin_token")) {
            api.get("/auth/me")
                .then((r) => setIsAdmin(r.data?.role === "admin" || !!r.data?.permissions?.manage_listings))
                .catch(() => {});
        }
    }, [id]);

    useSeo({
        title: item ? `${item.title} – Ogłoszenie` : undefined,
        description: item ? (item.content || "").slice(0, 160) : "",
        type: "article",
    });

    return (
        <div className="min-h-screen bg-[#FAF8F3]" data-testid="ogloszenie-detail">
            <Navbar />
            {notFound && (
                <main className="max-w-3xl mx-auto px-6 py-32 text-center">
                    <p className="text-gray-500">Nie znaleziono ogłoszenia.</p>
                    <Link to="/ogloszenia" className="text-[#0D4F3C] underline">Powrót do listy</Link>
                </main>
            )}
            {item && (
                <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12 pt-32">
                    <Link to="/ogloszenia" className="inline-flex items-center gap-2 text-sm text-[#0D4F3C] hover:text-[#D4A537] mb-6">
                        <ArrowLeft size={14} /> Wróć do ogłoszeń
                    </Link>
                    <motion.article
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7 md:p-10"
                    >
                        <div className="flex items-center gap-3 mb-4 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] uppercase tracking-wider font-semibold bg-[#D4A537]/20 text-[#B88A26] border border-[#D4A537]/30">
                                <Tag size={11} /> {labels[item.category] || item.category}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                                <Calendar size={11} /> {formatDateTime(item.created_at)}
                            </span>
                        </div>
                        <h1 className="font-serif text-3xl md:text-4xl text-[#092E22] leading-tight mb-5 break-words [overflow-wrap:anywhere]" data-testid="ogloszenie-title">{item.title}</h1>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base md:text-lg break-words [overflow-wrap:anywhere]" data-testid="ogloszenie-content">{item.content}</p>

                        <div className="mt-8 p-5 rounded-2xl bg-[#FAF8F3] border border-gray-100" data-testid="ogloszenie-contact">
                            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#D4A537] mb-2">Kontakt</p>
                            <div className="flex items-center gap-2 mb-2">
                                <User size={14} className="text-[#0D4F3C]" />
                                <span className="font-medium text-[#092E22]">{item.contact_name}</span>
                            </div>
                            {item.contact_email && (
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Mail size={14} className="text-[#0D4F3C]" />
                                    <a href={`mailto:${item.contact_email}`} className="text-sm text-[#0D4F3C] hover:text-[#D4A537] underline-offset-4 hover:underline" data-testid="ogloszenie-email">
                                        {item.contact_email}
                                    </a>
                                </div>
                            )}
                            {item.contact_phone && (
                                <div className="flex items-center gap-2">
                                    <Phone size={14} className="text-[#0D4F3C]" />
                                    <a href={`tel:${item.contact_phone}`} className="text-sm text-[#0D4F3C] hover:text-[#D4A537]" data-testid="ogloszenie-phone">
                                        {item.contact_phone}
                                    </a>
                                </div>
                            )}
                        </div>
                    </motion.article>

                    <div className="mt-12">
                        <Comments listingId={item.id} isAdmin={isAdmin} />
                    </div>
                </main>
            )}
            <Footer />
        </div>
    );
}
